import React from 'react';
import styled, { css } from 'styled-components';

/**
 * SagaRaven.tsx — minimal WebP-only version
 *
 * ✅ Only includes the animations we have right now (imported below)
 * ✅ Simple way to move between animations (Prev/Next overlay controls)
 * ✅ Single source of truth for enable/disable via env + localStorage
 *
 * Usage:
 *   <SagaRaven controls />
 *   <SagaRaven animation="preening" />
 *   <SagaRaven animationIndex={3} onAnimationChange={(k,i)=>...} />
 *
 * Notes:
 * - Update the import paths to wherever the .webp files live in your repo.
 */

// ---- WebP assets (adjust paths to your project) ----------------------------
import ghost_costume from './assets/saga/ghost_costume.webp';
import head_tilt     from './assets/saga/head_tilt.webp';
import dance_1       from './assets/saga/dance_1.webp';
import idle_1        from './assets/saga/idle_1.webp';
import idle_2        from './assets/saga/idle_2.webp';
import idle_3        from './assets/saga/idle_3.webp';
import idle_4        from './assets/saga/idle_4.webp';
import idle_5        from './assets/saga/idle_5.webp';
import idle_6        from './assets/saga/idle_6.webp';
import idle_7        from './assets/saga/idle_7.webp';
import idle_8        from './assets/saga/idle_8.webp';
import drawing_1     from './assets/saga/drawing_1.webp';
import drawing_2     from './assets/saga/drawing_2.webp';
import drawing_3     from './assets/saga/drawing_3.webp';
import drawing_4     from './assets/saga/drawing_4.webp';
import drawing_5     from './assets/saga/drawing_5.webp';
import drawing_6     from './assets/saga/drawing_6.webp';
import drawing_7     from './assets/saga/drawing_7.webp';
import drawing_8     from './assets/saga/drawing_8.webp';
// Coloring assets
import coloring_1    from './assets/saga/coloring/coloring_1.webp';
import coloring_2    from './assets/saga/coloring/coloring_2.webp';
import coloring_3    from './assets/saga/coloring/coloring_3.webp';
import coloring_4    from './assets/saga/coloring/coloring_4.webp';
import coloring_5    from './assets/saga/coloring/coloring_5.webp';
import idle_slight_zoom from './assets/saga/idle_slight_zoom.webp';
import idle_swaying     from './assets/saga/idle_swaying.webp';
import leave_then_peek   from './assets/saga/leave_then_peek.webp';
import leave_then_peek_2 from './assets/saga/leave_then_peek_2.webp';
import preening       from './assets/saga/preening.webp';
import teaching       from './assets/saga/Teaching.webp';
import walking        from './assets/saga/walking.webp';
import idle_base        from './assets/saga/idle_1.webp';

// ---- Nightcap WebP assets (11pm–6am local) -------------------------------
import nightcap_idle_1 from './assets/saga/nightcap/nightcap_idle1.webp';
import nightcap_idle_2 from './assets/saga/nightcap/nightcap_idle2.webp';
import nightcap_idle_3 from './assets/saga/nightcap/nightcap_idle3.webp';
import nightcap_idle_4 from './assets/saga/nightcap/nightcap_idle4.webp';
import nightcap_idle_5 from './assets/saga/nightcap/nightcap_idle5.webp';
import nightcap_idle_6 from './assets/saga/nightcap/nightcap_idle6.webp';
import nightcap_idle_7 from './assets/saga/nightcap/nightcap_idle7.webp';
import nightcap_falling_1 from './assets/saga/nightcap/nightcap_fallingasleep1.webp';
import nightcap_sleeping_1 from './assets/saga/nightcap/nightcap_sleeping1.webp';
import nightcap_sleeping_2 from './assets/saga/nightcap/nightcap_sleeping2.webp';
import nightcap_sleeping_3 from './assets/saga/nightcap/nightcap_sleeping3.webp';
import nightcap_sleeping_4 from './assets/saga/nightcap/nightcap_sleeping4.webp';

// Registry of the animations you currently have, in a sensible browsing order
export const SAGA_ANIMATIONS = {
  ghost_costume,
  head_tilt,
  dance_1,
  idle_1,
  idle_2,
  idle_3,
  idle_4,
  idle_5,
  idle_6,
  idle_7,
  idle_8,
  drawing_1,
  drawing_2,
  drawing_3,
  drawing_4,
  drawing_5,
  drawing_6,
  drawing_7,
  drawing_8,
  coloring_1,
  coloring_2,
  coloring_3,
  coloring_4,
  coloring_5,
  idle_slight_zoom,
  idle_swaying,
  leave_then_peek,
  leave_then_peek_2,
  preening,
  teacher: teaching,
  walking,
  idle_base,
} as const;

export type SagaAnimKey = keyof typeof SAGA_ANIMATIONS;
export const SAGA_ANIMATION_KEYS = Object.keys(SAGA_ANIMATIONS) as SagaAnimKey[];

// Optional preloader to reduce first-view flashes
const preload = (urls: string[]) => {
  if (typeof Image === 'undefined') return;
  urls.forEach(u => { const img = new Image(); img.src = u; });
};
preload(SAGA_ANIMATION_KEYS.map(k => SAGA_ANIMATIONS[k]));

// Nightcap sequences (idle, transition, sleep)
const NIGHTCAP_IDLE = [
  nightcap_idle_1,
  nightcap_idle_2,
  nightcap_idle_3,
  nightcap_idle_4,
  nightcap_idle_5,
  nightcap_idle_6,
  nightcap_idle_7,
];
const NIGHTCAP_FALL = [
  // Single frame for now; supports future multi-frame
  nightcap_falling_1,
];
const NIGHTCAP_SLEEP = [
  nightcap_sleeping_1,
  nightcap_sleeping_2,
  nightcap_sleeping_3,
  nightcap_sleeping_4,
];
preload([...NIGHTCAP_IDLE, ...NIGHTCAP_FALL, ...NIGHTCAP_SLEEP]);

// ---- Public API ------------------------------------------------------------
export type SagaRavenSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';
export type SagaRavenPosition = 'inline' | 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'center';

export const SAGA_SINGLETON_PRIORITIES = {
  ambient: 0,
  pageAccent: 20,
  loading: 80,
  tour: 100,
} as const;

export interface SagaRavenProps {
  /** Choose an animation by key (controlled) */
  animation?: SagaAnimKey;
  /** Or choose by index in SAGA_ANIMATION_KEYS (controlled) */
  animationIndex?: number;
  /** Uncontrolled initial index */
  defaultIndex?: number;
  /** Called whenever the displayed animation changes */
  onAnimationChange?: (key: SagaAnimKey, index: number) => void;

  /** Show Prev/Next overlay buttons to move between animations */
  controls?: boolean;

  size?: SagaRavenSize;
  position?: SagaRavenPosition;
  className?: string;
  style?: React.CSSProperties;

  /** Mirror the animation horizontally without a separate asset */
  mirror?: boolean;

  /** Feature-flag/user toggle (fallback: env + localStorage) */
  enabled?: boolean;

  /** Idle mixing behavior (only active when uncontrolled) */
  idleMixer?: boolean;
  /** Drawing mixing behavior (only active when uncontrolled). Cycles drawing_1..drawing_8 without repeats */
  drawingMixer?: boolean;
  /** Coloring mixing behavior (only active when uncontrolled). Cycles coloring_1..coloring_5 without repeats */
  coloringMixer?: boolean;
  /** Base idle animation key; used most of the time */
  idleBase?: SagaAnimKey;
  /** Alternate animations to occasionally switch to */
  idleVariants?: SagaAnimKey[];
  /** Probability [0..1] to show base on each switch cycle */
  idleBaseWeight?: number;
  /** Min interval between auto-switches (ms) */
  idleMinIntervalMs?: number;
  /** Max interval between auto-switches (ms) */
  idleMaxIntervalMs?: number;

  /** Fade duration between animation swaps (ms). Set 0 to disable */
  transitionMs?: number;

  /** When true, participate in a singleton group so only one Saga is visible */
  singleton?: boolean;
  /** Group name for singleton coordination (default: 'global') */
  singletonGroup?: string;
  /** Higher priority wins visibility within the group */
  priority?: number;
  /** Automatically use Nightcap animations 11pm–6am local */
  nightcapAuto?: boolean;
  /** Force Nightcap regardless of time (for testing) */
  forceNightcap?: boolean;
}

// ---- Simple singleton coordinator (per process) ---------------------------
type Subscriber = {
  id: string;
  priority: number;
  setActive: (v: boolean) => void;
  ts: number; // recency for tie-breaks
};

type Group = {
  subs: Map<string, Subscriber>;
  activeId?: string;
};

const SagaSingleton = (() => {
  const groups = new Map<string, Group>();
  let clock = 0;

  const ensure = (group: string): Group => {
    let g = groups.get(group);
    if (!g) { g = { subs: new Map() }; groups.set(group, g); }
    return g;
  };

  const recompute = (g: Group) => {
    let winner: Subscriber | undefined;
    for (const s of g.subs.values()) {
      if (!winner) { winner = s; continue; }
      if (s.priority > winner.priority) winner = s;
      else if (s.priority === winner.priority && s.ts > winner.ts) winner = s; // most recent wins
    }
    const nextId = winner?.id;
    for (const s of g.subs.values()) {
      try { s.setActive(s.id === nextId); } catch {}
    }
    g.activeId = nextId;
  };

  return {
    register(group: string, id: string, priority: number, setActive: (v: boolean) => void) {
      const g = ensure(group);
      g.subs.set(id, { id, priority, setActive, ts: ++clock });
      recompute(g);
    },
    unregister(group: string, id: string) {
      const g = ensure(group);
      const wasActive = g.activeId === id;
      g.subs.delete(id);
      if (wasActive) g.activeId = undefined;
      recompute(g);
      if (g.subs.size === 0) groups.delete(group);
    },
    update(group: string, id: string, priority: number) {
      const g = ensure(group);
      const s = g.subs.get(id);
      if (!s) return;
      s.priority = priority;
      s.ts = ++clock;
      recompute(g);
    }
  };
})();

// ---- helpers ---------------------------------------------------------------
const sizeToPx = (size: SagaRavenSize | undefined): number => {
  switch (size) {
    case 'xs': return 64;
    case 'sm': return 96;
    case 'md': return 128;
    case 'lg': return 192;
    case 'xl': return 256;
    default: return 96;
  }
};

const getEnabled = (override?: boolean): boolean => {
  if (typeof override === 'boolean') return override;
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const v = window.localStorage.getItem('showSagaRaven');
      if (v === 'false') return false;
      if (v === 'true') return true;
    }
  } catch {
    // ignore localStorage access failures
  }
  return true; // default ON
};

// Night hours helper: active from 23:00 (inclusive) until 06:00 (exclusive)
const isNightHours = (): boolean => {
  try {
    const h = new Date().getHours();
    return h >= 23 || h < 6;
  } catch {
    return false;
  }
};

// ---- styled ---------------------------------------------------------------
const Wrapper = styled.span<{ $sizePx: number; $position: SagaRavenPosition; $clickable: boolean; }>`
  display: inline-block;
  width: ${p => p.$sizePx}px;
  height: ${p => p.$sizePx}px;
  line-height: 0;
  position: relative;

  ${p => p.$position !== 'inline' && css`
    position: absolute;
    ${p.$position.includes('top') ? 'top: 0;' : ''}
    ${p.$position.includes('bottom') ? 'bottom: 0;' : ''}
    ${p.$position.includes('left') ? 'left: 0;' : ''}
    ${p.$position.includes('right') ? 'right: 0;' : ''}
    ${p.$position === 'center' ? 'top: 50%; left: 50%; transform: translate(-50%, -50%);' : ''}
  `}
`;

const Img = styled.img<{ $mirror?: boolean }>`
  width: 100%;
  height: 100%;
  object-fit: contain;
  image-rendering: -webkit-optimize-contrast;
  will-change: opacity, transform;
  position: absolute;
  inset: 0;
  transform: ${p => (p.$mirror ? 'scaleX(-1)' : 'none')};
  transform-origin: center;
`;

const Controls = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  pointer-events: none;
`;

const Btn = styled.button`
  pointer-events: all;
  background: rgba(0,0,0,0.5);
  color: #fff;
  border: 0;
  border-radius: 999px;
  width: 22px;
  height: 22px;
  line-height: 22px;
  font-size: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

// ---- Component -------------------------------------------------------------
export const SagaRaven: React.FC<SagaRavenProps> = ({
  animation,
  animationIndex,
  defaultIndex,
  onAnimationChange,
  controls = false,
  size = 'md',
  position = 'inline',
  className,
  style,
  mirror = false,
  enabled,
  idleMixer = false,
  drawingMixer = false,
  coloringMixer = false,
  idleBase = 'idle_base' as SagaAnimKey,
  idleVariants,
  idleBaseWeight = 0.75,
  idleMinIntervalMs = 9000,
  idleMaxIntervalMs = 18000,
  transitionMs = 200,
  singleton = true,
  singletonGroup = 'global',
  priority = 0,
  nightcapAuto = false,
  forceNightcap = false,
}) => {
  const isEnabled = getEnabled(enabled);
  const participatesInSingleton = singleton && isEnabled;
  const sizePx = sizeToPx(size);

  // Nightcap mode (time-based or forced)
  const [isNightcapTime, setIsNightcapTime] = React.useState<boolean>(
    forceNightcap || (nightcapAuto && isNightHours())
  );
  React.useEffect(() => {
    if (forceNightcap) { setIsNightcapTime(true); return; }
    if (!nightcapAuto) { setIsNightcapTime(false); return; }
    setIsNightcapTime(isNightHours());
    const iv = window.setInterval(() => setIsNightcapTime(isNightHours()), 60_000);
    return () => { window.clearInterval(iv); };
  }, [nightcapAuto, forceNightcap]);
  const nightcapActive = isEnabled && (forceNightcap || (nightcapAuto && isNightcapTime));

  // singleton visibility state + fade management
  const [isActiveInGroup, setIsActiveInGroup] = React.useState(!participatesInSingleton);
  const [isGroupRendered, setIsGroupRendered] = React.useState(!participatesInSingleton);
  const [groupOpacity, setGroupOpacity] = React.useState(participatesInSingleton ? 0 : 1);
  const fadeTimerRef = React.useRef<number | undefined>(undefined);
  // singleton visibility state
  const idRef = React.useRef<string>('');
  if (!idRef.current) idRef.current = Math.random().toString(36).slice(2);

  // --- WebP duration parsing + cache ---------------------------------------
  // Parses animated WebP and sums ANMF frame durations (in ms). Returns 0 if
  // static or unknown. We cache by src URL to avoid repeated fetches.
  const durationCacheRef = React.useRef<Map<string, number>>(new Map());

  const parseWebPDurationMs = React.useCallback(async (src: string): Promise<number> => {
    try {
      if (!src) return 0;
      if (durationCacheRef.current.has(src)) return durationCacheRef.current.get(src)!;
      if (typeof fetch === 'undefined') return 0;
      const res = await fetch(src);
      if (!res.ok) return 0;
      const buf = await res.arrayBuffer();
      const bytes = new Uint8Array(buf);
      const view = new DataView(buf);

      // RIFF header: 'RIFF'(0-3), size(4-7), 'WEBP'(8-11)
      if (bytes.length < 12) return 0;
      const riff = String.fromCharCode(bytes[0], bytes[1], bytes[2], bytes[3]);
      const webp = String.fromCharCode(bytes[8], bytes[9], bytes[10], bytes[11]);
      if (riff !== 'RIFF' || webp !== 'WEBP') return 0;

      let p = 12;
      let total = 0;
      let loopCount: number | null = null;
      while (p + 8 <= bytes.length) {
        const id = String.fromCharCode(bytes[p], bytes[p + 1], bytes[p + 2], bytes[p + 3]);
        const size = view.getUint32(p + 4, true); // little-endian
        const dataStart = p + 8;
        const dataEnd = dataStart + size;
        if (dataEnd > bytes.length) break;

        if (id === 'ANIM') {
          // ANIM payload: 4 bytes BG color, 2 bytes loop count (LE)
          if (size >= 6) {
            loopCount = view.getUint16(dataStart + 4, true);
          }
        }
        if (id === 'ANMF') {
          // ANMF payload header is 16 bytes; duration is 3 bytes at offset 12..14 (little-endian)
          if (size >= 16) {
            const d0 = bytes[dataStart + 12] || 0;
            const d1 = bytes[dataStart + 13] || 0;
            const d2 = bytes[dataStart + 14] || 0;
            const dur = d0 + (d1 << 8) + (d2 << 16);
            total += dur;
          }
        }

        // chunks are padded to even sizes
        p = dataEnd + (size % 2);
      }

      // If loopCount is specified (>0), total duration is per-loop * loopCount.
      const finalDuration = (loopCount && loopCount > 0 ? total * loopCount : total);
      durationCacheRef.current.set(src, finalDuration);
      return finalDuration;
    } catch {
      return 0;
    }
  }, []);

  

  // --- Nightcap state machine ----------------------------------------------
  type NightcapPhase = 'idle' | 'falling' | 'sleeping' | 'waking';
  const [ncPhase, setNcPhase] = React.useState<NightcapPhase>('idle');
  const [ncIdleIdx, setNcIdleIdx] = React.useState(0);
  const [ncSleepIdx, setNcSleepIdx] = React.useState(0);
  const [ncFallIdx, setNcFallIdx] = React.useState(0);
  const [ncShownSrc, setNcShownSrc] = React.useState<string>(NIGHTCAP_IDLE[0]);
  const [ncPrevSrc, setNcPrevSrc] = React.useState<string | null>(null);
  const [ncOpacity, setNcOpacity] = React.useState(1);
  const ncTransitioningRef = React.useRef(false);

  const switchNightcapTo = React.useCallback((nextSrc: string) => {
    if (!transitionMs || transitionMs <= 0) {
      setNcPrevSrc(null);
      setNcShownSrc(nextSrc);
      setNcOpacity(1);
      return;
    }
    let tIn: number | undefined;
    let tCleanup: number | undefined;
    ncTransitioningRef.current = true;
    setNcPrevSrc(ncShownSrc);
    setNcShownSrc(nextSrc);
    setNcOpacity(0);
    tIn = window.setTimeout(() => setNcOpacity(1), 16);
    tCleanup = window.setTimeout(() => {
      setNcPrevSrc(null);
      ncTransitioningRef.current = false;
    }, Math.max(0, transitionMs) + 20);
    return () => {
      if (tIn !== undefined) window.clearTimeout(tIn);
      if (tCleanup !== undefined) window.clearTimeout(tCleanup);
      setNcOpacity(1);
      setNcPrevSrc(null);
      ncTransitioningRef.current = false;
    };
  }, [ncShownSrc, transitionMs]);

  // Nightcap scheduler
  React.useEffect(() => {
    if (!nightcapActive) return;
    let cancelled = false;
    let timer: number | undefined;

    const SMALL_CHANCE = 0.08; // idle -> falling chance per swap
    const WAKE_CHANCE = 0.35;  // sleeping -> waking chance per frame

    const step = async () => {
      if (cancelled) return;
      // Determine current source to base duration on
      const currentSrc = ncShownSrc;
      const dur = await parseWebPDurationMs(currentSrc);
      if (cancelled) return;
      const min = Math.max(0, idleMinIntervalMs || 0);
      const max = Math.max(min + 1, idleMaxIntervalMs || (min + 1));
      const fallback = Math.floor(Math.random() * (max - min)) + min;
      const delay = Math.max(0, dur || fallback);

      timer = window.setTimeout(() => {
        if (cancelled) return;
        if (ncTransitioningRef.current) { step(); return; }

        if (ncPhase === 'idle') {
          // Next idle frame or transition to falling asleep
          const willFall = Math.random() < SMALL_CHANCE;
          if (willFall) {
            setNcPhase('falling');
            setNcFallIdx(0);
            switchNightcapTo(NIGHTCAP_FALL[0]);
          } else {
            const next = (ncIdleIdx + 1) % NIGHTCAP_IDLE.length;
            setNcIdleIdx(next);
            switchNightcapTo(NIGHTCAP_IDLE[next]);
          }
        } else if (ncPhase === 'falling') {
          const next = ncFallIdx + 1;
          if (next < NIGHTCAP_FALL.length) {
            setNcFallIdx(next);
            switchNightcapTo(NIGHTCAP_FALL[next]);
          } else {
            setNcPhase('sleeping');
            setNcSleepIdx(0);
            switchNightcapTo(NIGHTCAP_SLEEP[0]);
          }
        } else if (ncPhase === 'sleeping') {
          // After each sleeping frame, chance to wake
          const willWake = Math.random() < WAKE_CHANCE;
          if (willWake) {
            setNcPhase('waking');
            setNcFallIdx(Math.max(0, NIGHTCAP_FALL.length - 1));
            switchNightcapTo(NIGHTCAP_FALL[Math.max(0, NIGHTCAP_FALL.length - 1)]);
          } else {
            const next = (ncSleepIdx + 1) % NIGHTCAP_SLEEP.length;
            setNcSleepIdx(next);
            switchNightcapTo(NIGHTCAP_SLEEP[next]);
          }
        } else if (ncPhase === 'waking') {
          const next = ncFallIdx - 1;
          if (next >= 0) {
            setNcFallIdx(next);
            switchNightcapTo(NIGHTCAP_FALL[next]);
          } else {
            setNcPhase('idle');
            // resume idle at current index (or 0)
            switchNightcapTo(NIGHTCAP_IDLE[ncIdleIdx % NIGHTCAP_IDLE.length]);
          }
        }

        step();
      }, delay);
    };

    // Initialize displayed src based on current phase
    if (ncPhase === 'idle') switchNightcapTo(NIGHTCAP_IDLE[ncIdleIdx % NIGHTCAP_IDLE.length]);
    if (ncPhase === 'sleeping') switchNightcapTo(NIGHTCAP_SLEEP[ncSleepIdx % NIGHTCAP_SLEEP.length]);
    if (ncPhase === 'falling') switchNightcapTo(NIGHTCAP_FALL[ncFallIdx % NIGHTCAP_FALL.length]);
    if (ncPhase === 'waking') switchNightcapTo(NIGHTCAP_FALL[Math.max(0, Math.min(ncFallIdx, NIGHTCAP_FALL.length - 1))]);

    step();
    return () => {
      cancelled = true;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [nightcapActive, ncPhase, ncIdleIdx, ncSleepIdx, ncFallIdx, idleMinIntervalMs, idleMaxIntervalMs, transitionMs, parseWebPDurationMs, switchNightcapTo]);

  // Register/unregister with singleton manager based on enabled and flag
  React.useEffect(() => {
    if (!participatesInSingleton) {
      // ensure hidden if not participating
      setIsActiveInGroup(true);
      return;
    }
    SagaSingleton.register(singletonGroup, idRef.current, priority, setIsActiveInGroup);
    return () => {
      SagaSingleton.unregister(singletonGroup, idRef.current);
    };
  }, [participatesInSingleton, singletonGroup]);

  // Update priority dynamically
  React.useEffect(() => {
    if (!participatesInSingleton) return;
    SagaSingleton.update(singletonGroup, idRef.current, priority);
  }, [priority, participatesInSingleton, singletonGroup]);

  // Fade logic when arbitration changes active state
  React.useEffect(() => {
    if (!participatesInSingleton) {
      // not participating -> ensure shown and clear timers
      if (fadeTimerRef.current !== undefined) window.clearTimeout(fadeTimerRef.current);
      setIsGroupRendered(true);
      setGroupOpacity(1);
      return;
    }
    if (isActiveInGroup) {
      // becoming active: render and fade in
      if (fadeTimerRef.current !== undefined) window.clearTimeout(fadeTimerRef.current);
      setIsGroupRendered(true);
      // next tick to ensure mount before fade-in
      const t = window.setTimeout(() => setGroupOpacity(1), 16);
      fadeTimerRef.current = t as unknown as number;
    } else {
      // becoming inactive: fade out, then unrender
      if (fadeTimerRef.current !== undefined) window.clearTimeout(fadeTimerRef.current);
      if (transitionMs && transitionMs > 0) {
        setGroupOpacity(0);
        const t = window.setTimeout(() => {
          setIsGroupRendered(false);
        }, transitionMs + 20);
        fadeTimerRef.current = t as unknown as number;
      } else {
        // no transition, hide immediately
        setIsGroupRendered(false);
      }
    }
  }, [isActiveInGroup, participatesInSingleton, transitionMs]);

  // controlled vs uncontrolled index
  const controlledIndex = animationIndex ?? (animation ? SAGA_ANIMATION_KEYS.indexOf(animation) : undefined);
  const isControlled = controlledIndex != null && controlledIndex >= 0;
  const [uncontrolledIndex, setUncontrolledIndex] = React.useState(() => {
    // If caller supplied an explicit default index, honor it
    if (typeof defaultIndex === 'number') {
      const idx0 = Math.max(0, Math.min(SAGA_ANIMATION_KEYS.length - 1, defaultIndex));
      return idx0;
    }

    // If using coloring mixer, start on a coloring or drawing animation instead of idle
    if (coloringMixer) {
      const colorings: SagaAnimKey[] = [
        'coloring_1','coloring_2','coloring_4','coloring_5'
      ].filter(k => SAGA_ANIMATION_KEYS.includes(k as SagaAnimKey)) as SagaAnimKey[];
      if (colorings.length > 0) {
        const pick = colorings[Math.floor(Math.random() * colorings.length)];
        const idxPick = SAGA_ANIMATION_KEYS.indexOf(pick);
        if (idxPick >= 0) return idxPick;
      }
    }

    if (drawingMixer) {
      const drawings: SagaAnimKey[] = [
        'drawing_1','drawing_2','drawing_3','drawing_4','drawing_5','drawing_6','drawing_7','drawing_8'
      ].filter(k => SAGA_ANIMATION_KEYS.includes(k as SagaAnimKey)) as SagaAnimKey[];
      if (drawings.length > 0) {
        const pick = drawings[Math.floor(Math.random() * drawings.length)];
        const idxPick = SAGA_ANIMATION_KEYS.indexOf(pick);
        if (idxPick >= 0) return idxPick;
      }
    }

    // Fallback to idle base
    const baseIdx = SAGA_ANIMATION_KEYS.indexOf(idleBase as SagaAnimKey);
    const prefer = (baseIdx >= 0 ? baseIdx : 0);
    const idx0 = Math.max(0, Math.min(SAGA_ANIMATION_KEYS.length - 1, prefer));
    return idx0;
  });
  const idx = isControlled ? controlledIndex! : uncontrolledIndex;

  const targetKey = SAGA_ANIMATION_KEYS[Math.max(0, Math.min(SAGA_ANIMATION_KEYS.length - 1, idx))];
  const targetSrc = SAGA_ANIMATIONS[targetKey];

  // Decouple displayed image from target to allow fade-through transitions
  const [shownIndex, setShownIndex] = React.useState(idx);
  const [prevIndex, setPrevIndex] = React.useState<number | null>(null);
  const shownKey = SAGA_ANIMATION_KEYS[Math.max(0, Math.min(SAGA_ANIMATION_KEYS.length - 1, shownIndex))];
  const shownSrc = SAGA_ANIMATIONS[shownKey];
  const [opacity, setOpacity] = React.useState(1);
  const transitioningRef = React.useRef(false);

  // Track last shown key to avoid immediate repeats in idle mixer
  const lastKeyRef = React.useRef<SagaAnimKey | null>(null);
  React.useEffect(() => { lastKeyRef.current = shownKey; }, [shownKey]);

  const emitChange = (key: SagaAnimKey, i: number) => onAnimationChange?.(key, i);

  const go = (delta: number) => {
    const next = (idx + delta + SAGA_ANIMATION_KEYS.length) % SAGA_ANIMATION_KEYS.length;
    if (controlledIndex != null) {
      // in controlled mode we just emit; parent should update props
      emitChange(SAGA_ANIMATION_KEYS[next], next);
    } else {
      setUncontrolledIndex(next);
      emitChange(SAGA_ANIMATION_KEYS[next], next);
    }
  };

  // Handle fade transition when target changes
  React.useEffect(() => {
    if (shownIndex === idx) return;
    if (!transitionMs || transitionMs <= 0) {
      setShownIndex(idx);
      setPrevIndex(null);
      return;
    }
    let tIn: number | undefined;
    let tCleanup: number | undefined;
    transitioningRef.current = true;
    const prev = shownIndex;
    setPrevIndex(prev);
    setShownIndex(idx);
    setOpacity(0);
    // Crossfade: raise current opacity next frame
    tIn = window.setTimeout(() => setOpacity(1), 16);
    // Cleanup: drop prev layer after fade completes
    tCleanup = window.setTimeout(() => {
      setPrevIndex(null);
      transitioningRef.current = false;
    }, Math.max(0, transitionMs) + 20);
    return () => {
      if (tIn !== undefined) window.clearTimeout(tIn);
      if (tCleanup !== undefined) window.clearTimeout(tCleanup);
      setOpacity(1);
      setPrevIndex(null);
      transitioningRef.current = false;
    };
  }, [idx, shownIndex, transitionMs]);

  // Idle mixer: occasionally switch animations when uncontrolled
  React.useEffect(() => {
    // Suppress idle mixer if drawing mixer or nightcap is active
    if (!idleMixer || drawingMixer || coloringMixer || isControlled || !isEnabled || nightcapActive) return;
    let timer: number | undefined;
    let cancelled = false;

    const variantsPool: SagaAnimKey[] = (idleVariants && idleVariants.length ? idleVariants : [
      'head_tilt', 'preening', 'dance_1', 'idle_2', 'idle_3', 'idle_4', 'idle_5', 'idle_6', 'idle_7', 'idle_8'
    ]) as SagaAnimKey[];

    const schedule = async () => {
      // Use the current target's animation duration if available; fallback to min/max window
      const dur = await parseWebPDurationMs(SAGA_ANIMATIONS[targetKey]);
      if (cancelled) return;
      const min = Math.max(0, idleMinIntervalMs || 0);
      const max = Math.max(min + 1, idleMaxIntervalMs || (min + 1));
      const fallback = Math.floor(Math.random() * (max - min)) + min;
      const delay = Math.max(0, dur || fallback);
      timer = window.setTimeout(() => {
        if (transitioningRef.current) { schedule(); return; }
        const last = lastKeyRef.current;
        const currentTarget = targetKey;
        const roll = Math.random();

        let chosen: SagaAnimKey | null = null;

        if (roll < idleBaseWeight) {
          // Prefer base, unless it would repeat
          if (idleBase !== last) {
            chosen = idleBase as SagaAnimKey;
          } else {
            const candidates = variantsPool.filter(k => k !== last && k !== currentTarget);
            if (candidates.length > 0) {
              chosen = candidates[Math.floor(Math.random() * candidates.length)];
            }
          }
        } else {
          // Prefer variants, excluding last
          const candidates = variantsPool.filter(k => k !== last && k !== currentTarget);
          if (candidates.length > 0) {
            chosen = candidates[Math.floor(Math.random() * candidates.length)];
          } else if (idleBase !== last) {
            chosen = idleBase as SagaAnimKey;
          }
        }

        if (chosen) {
          const nextIndex = SAGA_ANIMATION_KEYS.indexOf(chosen);
          if (nextIndex >= 0 && nextIndex !== idx) setUncontrolledIndex(nextIndex);
        }
        schedule();
      }, delay);
    };

    schedule();
    return () => {
      cancelled = true;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [idleMixer, idleBase, idleVariants, idleBaseWeight, idleMinIntervalMs, idleMaxIntervalMs, isControlled, isEnabled, targetKey, parseWebPDurationMs]);

  // Drawing mixer: randomly cycle through drawing_1..drawing_8 without immediate repeats
  React.useEffect(() => {
    if (!drawingMixer || isControlled || !isEnabled || nightcapActive) return;
    let timer: number | undefined;
    let cancelled = false;

    const drawings: SagaAnimKey[] = [
      'drawing_1', 'drawing_2', 'drawing_3', 'drawing_4', 'drawing_5', 'drawing_6', 'drawing_7', 'drawing_8'
    ] as SagaAnimKey[];

    const schedule = async () => {
      // Use the current target's animation duration if available; fallback to min/max window
      const dur = await parseWebPDurationMs(SAGA_ANIMATIONS[targetKey]);
      if (cancelled) return;
      const min = Math.max(0, idleMinIntervalMs || 0);
      const max = Math.max(min + 1, idleMaxIntervalMs || (min + 1));
      const fallback = Math.floor(Math.random() * (max - min)) + min;
      const delay = Math.max(0, dur || fallback);
      timer = window.setTimeout(() => {
        if (transitioningRef.current) { schedule(); return; }
        const last = lastKeyRef.current;
        const currentTarget = targetKey;
        const candidates = drawings.filter(k => k !== last && k !== currentTarget);
        const chosen = candidates[Math.floor(Math.random() * candidates.length)];
        if (chosen) {
          const nextIndex = SAGA_ANIMATION_KEYS.indexOf(chosen);
          if (nextIndex >= 0 && nextIndex !== idx) setUncontrolledIndex(nextIndex);
        }
        schedule();
      }, delay);
    };

    schedule();
    return () => {
      cancelled = true;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [drawingMixer, idleMinIntervalMs, idleMaxIntervalMs, isControlled, isEnabled, targetKey, parseWebPDurationMs]);

  // Coloring mixer: randomly cycle through coloring_1..coloring_5 without immediate repeats
  React.useEffect(() => {
    if (!coloringMixer || isControlled || !isEnabled || nightcapActive) return;
    let timer: number | undefined;
    let cancelled = false;

    const colorings: SagaAnimKey[] = [
      'coloring_1','coloring_2','coloring_3','coloring_4','coloring_5'
    ] as SagaAnimKey[];

    const schedule = async () => {
      const dur = await parseWebPDurationMs(SAGA_ANIMATIONS[targetKey]);
      if (cancelled) return;
      const min = Math.max(0, idleMinIntervalMs || 0);
      const max = Math.max(min + 1, idleMaxIntervalMs || (min + 1));
      const fallback = Math.floor(Math.random() * (max - min)) + min;
      const delay = Math.max(0, dur || fallback);
      timer = window.setTimeout(() => {
        if (transitioningRef.current) { schedule(); return; }
        const last = lastKeyRef.current;
        const currentTarget = targetKey;
        const candidates = colorings.filter(k => k !== last && k !== currentTarget);
        const chosen = candidates[Math.floor(Math.random() * candidates.length)];
        if (chosen) {
          const nextIndex = SAGA_ANIMATION_KEYS.indexOf(chosen);
          if (nextIndex >= 0 && nextIndex !== idx) setUncontrolledIndex(nextIndex);
        }
        schedule();
      }, delay);
    };

    schedule();
    return () => {
      cancelled = true;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [coloringMixer, idleMinIntervalMs, idleMaxIntervalMs, isControlled, isEnabled, targetKey, parseWebPDurationMs]);

  if (!isEnabled) return null;
  if (participatesInSingleton && !isGroupRendered) return null;

  // Merge caller styles with group opacity transition
  const mergedStyle: React.CSSProperties | undefined = {
    ...(style || {}),
    ...(participatesInSingleton ? { opacity: groupOpacity, transition: `opacity ${Math.max(0, transitionMs)}ms ease-in-out${style?.transition ? `, ${style.transition}` : ''}` } : {}),
  };

  return (
    <Wrapper className={className} style={mergedStyle} $sizePx={sizePx} $position={position} $clickable={false}>
      {nightcapActive ? (
        <>
          {ncPrevSrc && (
            <Img
              src={ncPrevSrc}
              alt=""
              aria-hidden="true"
              data-saga-animation="saga-nightcap-prev"
              $mirror={mirror}
              style={{ opacity: 1 - ncOpacity, transition: `opacity ${Math.max(0, transitionMs)}ms ease-in-out` }}
            />
          )}
          <Img
            src={ncShownSrc}
            alt=""
            aria-hidden="true"
            data-saga-animation="saga-nightcap"
            $mirror={mirror}
            style={{ opacity: ncOpacity, transition: `opacity ${Math.max(0, transitionMs)}ms ease-in-out` }}
          />
        </>
      ) : (
        <>
          {prevIndex != null && (() => {
            const prevKey = SAGA_ANIMATION_KEYS[Math.max(0, Math.min(SAGA_ANIMATION_KEYS.length - 1, prevIndex))];
            const prevSrc = SAGA_ANIMATIONS[prevKey];
            return (
              <Img
                src={prevSrc}
                alt=""
                aria-hidden="true"
                data-saga-animation={prevKey}
                $mirror={mirror}
                style={{ opacity: 1 - opacity, transition: `opacity ${Math.max(0, transitionMs)}ms ease-in-out` }}
              />
            );
          })()}
          <Img
            src={shownSrc}
            alt=""
            aria-hidden="true"
            data-saga-animation={shownKey}
            $mirror={mirror}
            style={{ opacity, transition: `opacity ${Math.max(0, transitionMs)}ms ease-in-out` }}
          />
        </>
      )}
      {controls && !nightcapActive && (
        <Controls>
          <Btn type="button" aria-label="Previous animation" onClick={() => go(-1)}>‹</Btn>
          <Btn type="button" aria-label="Next animation" onClick={() => go(1)}>›</Btn>
        </Controls>
      )}
    </Wrapper>
  );
};

export default SagaRaven;
